import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  doFetchBillingData,
  doCancelSubscription,
  doCloseCardFailureError,
} from 'ducks/billing/operations'
import { selectCardError } from 'ducks/billing/selectors'
import { selectPlans } from 'ducks/billing/selectors/selectPlans'
import { selectBillingUsage } from 'ducks/billing/selectors/selectBillingUsage'
import { isPlanSmaller, isPlanLimitsExceeded } from 'ducks/billing/util'

const emptyModalState = {
  downgradeKb: false,
  downgradeInbox: false,
  cancellation: false,
  limitKb: false,
  limitInbox: false,
}

export const useBillingModals = ({ onSave }) => {
  const dispatch = useDispatch()
  const plans = useSelector(selectPlans)
  const usage = useSelector(selectBillingUsage)
  const [modals, setModals] = useState(emptyModalState)
  const cardError = useSelector(selectCardError)
  const [lastChanges, setLastChanges] = useState({})

  const cancelSave = useCallback(() => {
    setModals(emptyModalState)
  }, [])

  const saveChanges = useCallback(
    inputChanges => {
      const changes = inputChanges.form ? inputChanges : lastChanges
      setModals({
        downgradeKb: false,
        downgradeInbox: false,
        cancellation: false,
        limitKb: false,
        limitInbox: false,
      })
      if (onSave) onSave(changes.form)
      setLastChanges({})
    },
    [onSave, lastChanges]
  )

  const checkKbAndSave = useCallback(
    inputChanges => {
      const changes = inputChanges.form ? inputChanges : lastChanges
      const { currentKbPlan, newKbPlan } = changes

      if (
        currentKbPlan.planId !== newKbPlan.planId &&
        isPlanSmaller(currentKbPlan, newKbPlan)
      ) {
        setModals({
          downgradeKb: true,
          downgradeInbox: false,
          cancellation: false,
          limitKb: false,
          limitInbox: false,
        })
      } else {
        saveChanges(changes)
      }
    },
    [saveChanges, lastChanges]
  )

  const checkInboxAndSave = useCallback(
    inputChanges => {
      const changes = inputChanges.form ? inputChanges : lastChanges
      const { currentInboxPlan, newInboxPlan } = changes

      if (
        currentInboxPlan.planId !== newInboxPlan.planId &&
        isPlanSmaller(currentInboxPlan, newInboxPlan)
      ) {
        setModals({
          downgradeKb: false,
          downgradeInbox: true,
          cancellation: false,
          limitKb: false,
          limitInbox: false,
        })
      } else {
        checkKbAndSave(changes)
      }
    },
    [checkKbAndSave, lastChanges]
  )

  const checkKbLimitAndSave = useCallback(
    inputChanges => {
      const changes = inputChanges.form ? inputChanges : lastChanges
      const { currentKbPlan, newKbPlan } = changes

      if (
        currentKbPlan.planId !== newKbPlan.planId &&
        isPlanSmaller(currentKbPlan, newKbPlan) &&
        isPlanLimitsExceeded(newKbPlan, usage.kb)
      ) {
        setModals({
          downgradeKb: false,
          downgradeInbox: false,
          cancellation: false,
          limitKb: true,
          limitInbox: false,
        })
      } else {
        checkInboxAndSave(changes)
      }
    },
    [usage, checkInboxAndSave, lastChanges]
  )

  const checkInboxLimitAndSave = useCallback(
    inputChanges => {
      const changes = inputChanges.form ? inputChanges : lastChanges
      const { currentInboxPlan, newInboxPlan } = changes

      if (
        currentInboxPlan.planId !== newInboxPlan.planId &&
        isPlanSmaller(currentInboxPlan, newInboxPlan) &&
        isPlanLimitsExceeded(newInboxPlan, usage.inbox)
      ) {
        setModals({
          downgradeKb: false,
          downgradeInbox: false,
          cancellation: false,
          limitKb: false,
          limitInbox: true,
        })
      } else {
        checkKbLimitAndSave(changes)
      }
    },
    [usage, checkKbLimitAndSave, lastChanges]
  )

  const reloadBillingAndSave = useCallback(
    async form => {
      const {
        billing: {
          inbox: { plan: inboxPlanId },
          kb: { plan: kbPlanId },
        },
      } = await dispatch(doFetchBillingData())

      const currentInboxPlan = plans.find(
        p => p.product === 'inbox' && p.planId === inboxPlanId
      )
      const newInboxPlan = plans.find(
        p => p.product === 'inbox' && p.planId === form.inboxPlanId
      )
      const currentKbPlan = plans.find(
        p => p.product === 'kb' && p.planId === kbPlanId
      )
      const newKbPlan = plans.find(
        p => p.product === 'kb' && p.planId === form.kbPlanId
      )

      const changes = {
        form,
        currentInboxPlan,
        newInboxPlan,
        currentKbPlan,
        newKbPlan,
      }
      setLastChanges(changes)
      checkInboxLimitAndSave(changes)
    },
    [dispatch, plans, checkInboxLimitAndSave]
  )

  const cancelAccount = useCallback(
    ({ lastCompletedStep, responses }) => {
      if (lastCompletedStep.triggerCancelAccount) {
        dispatch(doCancelSubscription(responses))
      }
    },
    [dispatch]
  )

  const closeCardFailureModal = useCallback(
    () => {
      dispatch(doCloseCardFailureError())
    },
    [dispatch]
  )

  const startCancellationFlow = useCallback(() => {
    setModals({
      downgradeKb: false,
      downgradeInbox: false,
      cancellation: true,
      limitKb: false,
      limitInbox: false,
    })
  }, [])

  return {
    modals,
    cardError,
    reloadBillingAndSave,
    checkInboxLimitAndSave,
    checkKbLimitAndSave,
    checkInboxAndSave,
    checkKbAndSave,
    saveChanges,
    cancelSave,
    cancelAccount,
    closeCardFailureModal,
    startCancellationFlow,
  }
}
